import { z } from 'zod';

/** Addresses are stored lowercased; normalize at the boundary so nothing
 *  downstream has to remember to. */
const emailField = z.string().email().max(255).transform((s) => s.toLowerCase());

export const createVendorSchema = z.object({
  businessName: z.string().min(1, 'Business name is required').max(255),
  contactName: z.string().max(255).nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  // Bare domains ("bloomfloral.com") are accepted; the frontend prepends the
  // scheme at render time, so a strict .url() here would reject valid input.
  website: z.string().max(2048).nullable().optional(),
  primaryCategory: z.string().max(100).nullable().optional(),
  secondaryCategories: z.array(z.string().min(1).max(100)).max(10).default([]),
  notes: z.string().max(2000).nullable().optional(),
  /** At least one is expected in practice but not required: PRD §F1 says a
   *  vendor can be created with only a name. */
  emails: z.array(emailField).max(10).default([]),
  /** Free-form, her words — a column header or price band, not a taxonomy.
   *  Capped short: a tag is a label, not a sentence. */
  tags: z.array(z.string().min(1).max(60)).max(20).default([]),
});

export const updateVendorSchema = createVendorSchema.partial().extend({
  // `emails` is managed through the dedicated vendor-emails routes so that
  // adding an address never implies deleting the others by omission.
  emails: z.undefined().optional(),
  archived: z.boolean().optional(),
});

export const addVendorEmailSchema = z.object({
  email: emailField,
  isPrimary: z.boolean().default(false),
});

/** CSV import: rows already mapped to fields by the client's column mapper. */
export const importVendorsSchema = z.object({
  rows: z
    .array(
      z.object({
        businessName: z.string().min(1).max(255),
        contactName: z.string().max(255).nullable().optional(),
        email: z.string().max(255).nullable().optional(),
        phone: z.string().max(50).nullable().optional(),
        website: z.string().max(2048).nullable().optional(),
        primaryCategory: z.string().max(100).nullable().optional(),
        notes: z.string().max(2000).nullable().optional(),
        /** Free-form, lifted from the planner's own file — a column header, a
         *  sheet name, a price band in her words. Not an enum: the axes
         *  planners organize by are not shared across files. */
        tags: z.array(z.string().min(1).max(60)).max(20).default([]),
        importSource: z.string().max(500).nullable().optional(),
      }),
    )
    .min(1)
    .max(2000),
  /** When false, rows whose email already exists are reported as skipped
   *  rather than merged. PRD §F1 wants the planner warned, not surprised. */
  mergeDuplicates: z.boolean().default(false),
});

/**
 * Task 12 of the rolodex-import plan: an import leaves `primaryCategory` null
 * rather than guessing it from the business name ("Sally Blue Florals" is
 * probably florals, which is right often enough to be trusted and wrong
 * quietly — the design spec rejected that tradeoff). This is the planner's
 * own correction, applied to as many uncategorized vendors at once as she
 * wants — capped at 500 because that is already generous for a rolodex this
 * plan sizes at "a few hundred, capped at a few thousand."
 */
export const bulkAssignCategorySchema = z.object({
  vendorIds: z.array(z.string().uuid()).min(1).max(500),
  primaryCategory: z.string().min(1).max(100),
});

export type CreateVendorInput = z.infer<typeof createVendorSchema>;
export type UpdateVendorInput = z.infer<typeof updateVendorSchema>;
export type AddVendorEmailInput = z.infer<typeof addVendorEmailSchema>;
export type ImportVendorsInput = z.infer<typeof importVendorsSchema>;
export type BulkAssignCategoryInput = z.infer<typeof bulkAssignCategorySchema>;
